//////////////////
// IMPORTS
//////////////////

// libraries
import React, { useCallback } from "react";
import { DateTime, Interval } from "luxon";
import { useRouter } from "found";

// queries, mutations, and types.
import { useQuery, useMutation } from "@apollo/client";
import {
  bookingQuery,
  cancelBookingMutation,
  CancelBookingPayload,
  CancelBookingVariables,
  SchedulingLinkBooking,
  SchedulingLinkBookingVariables,
} from "@clockwise/web-commons/src/ui/scheduling-link";

// components
import {
  SchedulingConfirmation,
  ConfirmationType,
} from "@clockwise/web-commons/src/ui/scheduling-confirmation";
import { RouteRenderer } from "@components/route-renderer";
import { PublicBooking } from "@clockwise/web-commons/src/ui/scheduling-link";
import BannerVariants from "./banner-variants";

// utils
import { getValue } from "@clockwise/web-commons/src/util/errorable.util";
import { getRenderTimeZone } from "@util/time-zone.util";

export const CancelBookingRenderer = () => {
  const { match } = useRouter();
  const timeZone = DateTime.local().zoneName;

  const bookingId = React.useMemo(() => match?.params?.["bookingId"] || "", [match]);
  const [bookingCancelled, setBookingCancelled] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState("");

  const { loading, data, error } = useQuery<SchedulingLinkBooking, SchedulingLinkBookingVariables>(
    bookingQuery,
    {
      variables: {
        bookingId,
        timeZone,
        startDate: DateTime.now().toISODate(),
        endDate: DateTime.now().toISODate(),
      },
    },
  );

  const [cancelBooking] = useMutation<CancelBookingPayload, CancelBookingVariables>(
    cancelBookingMutation,
    {
      fetchPolicy: "no-cache", // No need to refetch.
      onCompleted: () => {
        setBookingCancelled(true);
      },
    },
  );

  const cancelBookingWrapped = useCallback(
    async (reason?: string) => {
      await cancelBooking({
        variables: {
          bookingId,
          reason,
        },
      });
      if (reason) {
        setCancelReason(reason);
      }
    },
    [cancelBooking, bookingId, setCancelReason],
  );

  const publicBooking = getValue(data?.viewer?.schedulingLinkBooking);

  const getDuration = (booking: PublicBooking) =>
    Interval.fromISO(`${booking.startTime}/${booking.endTime}`).length("minutes");

  const getSelectedTime = (_publicBooking: PublicBooking) => ({
    bookedDateOrDateTime: _publicBooking.startTime,
    bookedTimeZone: getRenderTimeZone(),
  });

  const bookingLinkName = publicBooking?.schedulingLinkDetails?.linkName;
  const slug = publicBooking?.schedulingLinkDetails?.slug;
  const meetingSettingsId = publicBooking?.schedulingLinkDetails?.publicMeetingSettings.id;
  const isOwner =
    !!bookingLinkName &&
    bookingLinkName === getValue(data?.viewer.user?.linkNameErrorable)?.linkName;

  const isLoaded = publicBooking && !!bookingLinkName && !!slug && !!meetingSettingsId;

  return (
    <RouteRenderer
      loading={loading}
      loadingMessage="Manage Booking"
      notFound={!loading && !!error && error.toString().includes("no such booking")}
      error={!!error || (!loading && !isLoaded)}
    >
      {isLoaded && (
        <SchedulingConfirmation
          meetingSettings={{
            id: meetingSettingsId,
            name: publicBooking.meetingName,
            durationMinutes: getDuration(publicBooking),
            locationType: publicBooking.locationType,
            description: publicBooking.description,
          }}
          linkName={bookingLinkName}
          slug={slug}
          isOwner={isOwner}
          owner={publicBooking.ownerProfile}
          selectedTime={getSelectedTime(publicBooking)}
          onCancel={cancelBookingWrapped}
          completed={bookingCancelled}
          variant={ConfirmationType.Cancel}
          cancelRescheduleReason={cancelReason}
          banner={
            <BannerVariants
              ownerProfile={publicBooking.ownerProfile}
              isAuthenticatedUser={!!getValue(data?.viewer?.user)?.id}
            />
          }
        />
      )}
    </RouteRenderer>
  );
};
